import React, { createContext, useContext, ReactNode } from 'react';
import { useHttpClientIntegration } from '@/shared/infra/http/HttpClientContext';
import { ApiTncRepository } from '../repositories/ApiTncRepository';
import {
  GetTncCaseUseCase,
  StartTncProcessUseCase,
  UpdateEmployeeAvailabilityUseCase,
  SendFanDocumentUseCase,
  RecordEmployeeDecisionUseCase,
  SubmitSignaturesUseCase,
  DownloadRdcUseCase,
  CloseCaseUseCase,
  UpdateCaseDetailsUseCase,
  AcknowledgeTncInstructionsUseCase,
  UpdateTncDetailsUseCase,
} from '../../application/domain/use-cases';

interface TncDIContextType {
  tncRepository: ApiTncRepository;
  getTncCaseUseCase: GetTncCaseUseCase;
  startTncProcessUseCase: StartTncProcessUseCase;
  updateEmployeeAvailabilityUseCase: UpdateEmployeeAvailabilityUseCase;
  sendFanDocumentUseCase: SendFanDocumentUseCase;
  recordEmployeeDecisionUseCase: RecordEmployeeDecisionUseCase;
  submitSignaturesUseCase: SubmitSignaturesUseCase;
  downloadRdcUseCase: DownloadRdcUseCase;
  closeCaseUseCase: CloseCaseUseCase;
  updateCaseDetailsUseCase: UpdateCaseDetailsUseCase;
  acknowledgeTncInstructionsUseCase: AcknowledgeTncInstructionsUseCase;
  /** Primary integration use case — shared by all steps */
  updateTncDetailsUseCase: UpdateTncDetailsUseCase;
}

const TncDIContext = createContext<TncDIContextType | undefined>(undefined);

export const TncDIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const httpClient = useHttpClientIntegration();

  // Repository
  const tncRepository = new ApiTncRepository(httpClient);

  // Use Cases
  const getTncCaseUseCase = new GetTncCaseUseCase(tncRepository);
  const startTncProcessUseCase = new StartTncProcessUseCase(tncRepository);
  const updateEmployeeAvailabilityUseCase = new UpdateEmployeeAvailabilityUseCase(tncRepository);
  const sendFanDocumentUseCase = new SendFanDocumentUseCase(tncRepository);
  const recordEmployeeDecisionUseCase = new RecordEmployeeDecisionUseCase(tncRepository);
  const submitSignaturesUseCase = new SubmitSignaturesUseCase(tncRepository);
  const downloadRdcUseCase = new DownloadRdcUseCase(tncRepository);
  const closeCaseUseCase = new CloseCaseUseCase(tncRepository);
  const updateCaseDetailsUseCase = new UpdateCaseDetailsUseCase(tncRepository);
  const acknowledgeTncInstructionsUseCase = new AcknowledgeTncInstructionsUseCase(tncRepository);
  const updateTncDetailsUseCase = new UpdateTncDetailsUseCase(tncRepository);

  return (
    <TncDIContext.Provider
      value={{
        tncRepository,
        getTncCaseUseCase,
        startTncProcessUseCase,
        updateEmployeeAvailabilityUseCase,
        sendFanDocumentUseCase,
        recordEmployeeDecisionUseCase,
        submitSignaturesUseCase,
        downloadRdcUseCase,
        closeCaseUseCase,
        updateCaseDetailsUseCase,
        acknowledgeTncInstructionsUseCase,
        updateTncDetailsUseCase,
      }}
    >
      {children}
    </TncDIContext.Provider>
  );
};

export const useTncDI = (): TncDIContextType => {
  const context = useContext(TncDIContext);
  if (!context) {
    throw new Error('useTncDI must be used within TncDIProvider');
  }
  return context;
};
